import type { PayloadAction, WithSlice } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { rootReducer } from '~shared/store';
import { BookingRoom } from './booking.types';
import { BOOKING_STEPS } from './booking.const';
import {
  Offering,
  PopulatedOffering,
} from '~entities/offering/model/offering.types';
import { Payment } from 'src/features/payment/model/payment.types';
import {
  createNewBookingRoom,
  filterOfferings,
  getCurrentBookingRoom,
  getOrCreateCurrentBookingRoom,
  populateOfferings,
  sortOfferings,
} from '../lib/booking.utils';
import { Offer } from '~entities/offer/model/offer.types';
import { getOfferingById } from '~entities/offering/lib/offering.utils';
import { isLastMinutePackage } from '~entities/offering/lib/offering-type';
import {
  populateOffersWithRate,
  transformLastMinutePackageToOffer,
} from '~entities/offer/lib/offer.mappers';
import { RoomFeature } from '~entities/room/model/room.types';
import { configurationQueryOptions } from '~entities/configuration/api/configuration.queries';
import { queryClient } from '~shared/query-client';
import i18n, { Locale } from '~app/i18n';

type State = {
  rooms: (Partial<BookingRoom> & {
    id: string;
  })[];
  currentRoomId?: BookingRoom['id'];
  payment?: Payment;
  checkoutType: 'DEFAULT' | 'REQUEST';
  currentStep: (typeof BOOKING_STEPS)[number]['name'];
  offersList: {
    desiredOffering?: PopulatedOffering;
    otherOfferings: PopulatedOffering[];
    featuresFilter: RoomFeature[] | undefined;
    sorting: 'relevance' | 'low-to-high' | 'high-to-low';
  };
};

const initialState: State = {
  rooms: [],
  currentRoomId: undefined,
  payment: undefined,
  checkoutType: 'DEFAULT',
  currentStep: 'occupancy',
  offersList: {
    desiredOffering: undefined,
    otherOfferings: [],
    featuresFilter: [],
    sorting: 'relevance',
  },
} as const;

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    setOffers: (state, { payload: offers }: PayloadAction<Offer[]>) => {
      const configuration = queryClient.getQueryData(
        configurationQueryOptions(i18n.language as Locale).queryKey,
      );

      if (!configuration) return;

      const populatedOffers = populateOffersWithRate(offers) ?? [];

      const offerings = populateOfferings(populatedOffers);

      const currentBookingRoom = getCurrentBookingRoom(state);

      const desiredOffering = offerings?.find(
        item => item.id === currentBookingRoom?.offeringId,
      );

      const otherOfferings =
        offerings?.filter(item => item.id !== currentBookingRoom?.offeringId) ??
        [];

      state.offersList.desiredOffering = desiredOffering;
      state.offersList.otherOfferings = otherOfferings;
    },
    setSorting: (
      state,
      { payload: sorting }: PayloadAction<State['offersList']['sorting']>,
    ) => {
      state.offersList.sorting = sorting;
    },
    filterOffers: (
      state,
      {
        payload: featuresFilter,
      }: PayloadAction<State['offersList']['featuresFilter']>,
    ) => {
      state.offersList.featuresFilter = featuresFilter;
    },
    updateOffering: (state, { payload: offering }: PayloadAction<Offering>) => {
      const currentRoom = getOrCreateCurrentBookingRoom(state);
      currentRoom.offeringId = offering.id;
    },
    updateOffer: (state, { payload: offer }: PayloadAction<Offer>) => {
      const currentRoom = getOrCreateCurrentBookingRoom(state);
      currentRoom.offer = offer;
    },
    setCurrentRoom: (
      state,
      { payload: roomId }: PayloadAction<BookingRoom['id']>,
    ) => {
      state.currentRoomId = roomId;
      const currentRoom = getCurrentBookingRoom(state);
      state.currentStep =
        currentRoom && currentRoom.cartItemId ? 'checkout' : 'occupancy';
    },
    updateCurrentRoom: (
      state,
      { payload }: PayloadAction<Partial<BookingRoom>>,
    ) => {
      const currentRoom = getOrCreateCurrentBookingRoom(state);
      if (
        ('occupancy' in payload &&
          JSON.stringify(payload.occupancy) !==
            JSON.stringify(currentRoom.occupancy)) ||
        ('offeringId' in payload &&
          payload.offeringId !== currentRoom.offeringId)
      )
        currentRoom.offer = undefined;

      if ('id' in payload && currentRoom.id !== payload.id) {
        state.currentRoomId = payload.id;
      }

      Object.assign(currentRoom, payload);

      const offering = getOfferingById(currentRoom.offeringId);
      if (offering && currentRoom.offer) {
        state.offersList.desiredOffering = {
          ...offering,
          offers: [currentRoom.offer],
        };
      }

      if (isLastMinutePackage(offering))
        currentRoom.offer = transformLastMinutePackageToOffer(offering);
    },
    addRoom: (
      state,
      { payload }: PayloadAction<Partial<BookingRoom> | undefined>,
    ) => {
      createNewBookingRoom(state, payload);
      state.currentStep = 'occupancy';
    },
    removeRoom: (
      state,
      { payload: roomId }: PayloadAction<BookingRoom['id']>,
    ) => {
      state.rooms = state.rooms.filter(room => room.id !== roomId);
      if (state.currentRoomId === roomId) {
        state.currentRoomId = state.rooms[0]?.id;
        state.currentStep = 'occupancy';
      }
    },
    updateRooms: (state, { payload: rooms }: PayloadAction<BookingRoom[]>) => {
      state.rooms = [...state.rooms.filter(room => !room.cartItemId), ...rooms];
      if (state.currentRoomId === undefined) {
        state.currentRoomId = state.rooms[0]?.id;
      }
    },
    updatePayment: (
      state,
      { payload: payment }: PayloadAction<Payment | undefined>,
    ) => ({
      ...state,
      payment,
    }),
    setCurrentStep: (
      state,
      { payload: step }: PayloadAction<State['currentStep']>,
    ) => ({
      ...state,
      currentStep: step,
    }),
    setCheckoutType: (
      state,
      { payload: checkoutType }: PayloadAction<State['checkoutType']>,
    ) => ({
      ...state,
      checkoutType,
    }),
    resetState: () => initialState,
  },
  selectors: {
    currentStep: state => state.currentStep,
    payment: state => state.payment,
    checkoutType: state => state.checkoutType,
    rooms: state => state.rooms,
    currentRoom: state =>
      state.rooms.find(room => room.id === state.currentRoomId),
    offersList: state => state.offersList,
  },
});

export const bookingActions = bookingSlice.actions;

declare module '~shared/store' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof bookingSlice> {}
}

const injectedBookingSlice = bookingSlice.injectInto(rootReducer);

export const bookingSelectors = {
  ...injectedBookingSlice.selectors,
  selectOfferings: createSelector(
    [injectedBookingSlice.selectors.offersList],
    offersList => ({
      desiredOffering: offersList.desiredOffering,
      otherOfferings: offersList.otherOfferings,
      filteredOtherOfferings: sortOfferings(
        filterOfferings(offersList.otherOfferings, offersList.featuresFilter),
        offersList.sorting,
      ),
    }),
  ),
};
